import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppEventType } from '../enums/app-events.enum';
import { OdometerReadingMediaService } from './odometer-reading-media.service';
import { OdometerReadingService } from './odometer-reading.service';
import { StoreName } from '../entities/StoreName';
import { OdometerReadingMedia, UploadStatus } from '../entities/OdometerReadingMedia';
import { LogService } from '../baseServices/logging/log.service';
import { EventBusService } from './event-bus.service';
import { AppUserService } from './app-user.service';
import { RepositoryService } from '../baseServices/repository.service';

@Injectable({
  providedIn: 'root'
})
export class OdometerReadingMediaCleanupService implements OnDestroy {
  private subscriptions: Subscription[] = [];

  constructor(
    private eventBusService: EventBusService,
    private odometerReadingMediaService: OdometerReadingMediaService,
    private odometerReadingService: OdometerReadingService,
    private appUserService: AppUserService,
    private logService: LogService,
    private repository: RepositoryService
  ) {
    this.initializeEventListeners();
  }

  private initializeEventListeners(): void {
    const loginSuccessSubscription = this.eventBusService.on(AppEventType.LOGIN_SUCCESS, () => {
      this.performCleanup();
    });
    
    this.subscriptions.push(loginSuccessSubscription);
  }

  private async performCleanup(): Promise<void> {
    try {
      this.logService.info('OdometerReadingMediaCleanup.Started', {});

      // Get current user ID
      const currentUserId = this.appUserService.currentUserId;
      if (!currentUserId) {
        this.logService.warn('OdometerReadingMediaCleanup.NoCurrentUser', {});
        return;
      }

      // Start a transaction for all cleanup operations
      const transaction = await this.repository.startTransaction([StoreName.OdometerReadingMedia, StoreName.OdometerReading], 'readwrite');

      // Get all odometer reading media records
      const allMedia = await this.odometerReadingMediaService.getAllOdometerReadingMedia(transaction);
      
      // Get all odometer readings for the current user (including soft deleted ones for the check)
      const userOdometerReadings = await this.odometerReadingService.getOdometerReadingsByUserId(currentUserId, transaction, true);
      
      // Create a set of valid (non-soft-deleted) odometer reading record IDs
      const validOdometerReadingIds = new Set<number>();
      for (const reading of userOdometerReadings) {
        if (!this.odometerReadingService.checkIsOdometerReadingSoftDeleted(reading)) {
          validOdometerReadingIds.add(reading.RecordId);
        }
      }

      const mediaToDelete: OdometerReadingMedia[] = [];

      // Check all media records
      for (const media of allMedia) {
        const isOrphaned = !validOdometerReadingIds.has(media.OdometerReadingRecordId);
        const isUploaded = media.UploadStatus === UploadStatus.Uploaded;
        
        // Delete if successfully uploaded
        if (isUploaded) {
          mediaToDelete.push(media);
          this.logService.debug('OdometerReadingMediaCleanup.UploadedMediaFound', { 
            mediaRecordId: media.RecordId, 
            odometerReadingRecordId: media.OdometerReadingRecordId,
            uploadStatus: media.UploadStatus
          });
        }
        // Delete if orphaned (but don't touch Failed or PendingUpload that aren't orphaned)
        else if (isOrphaned) {
          mediaToDelete.push(media);
          this.logService.debug('OdometerReadingMediaCleanup.OrphanedMediaFound', { 
            mediaRecordId: media.RecordId, 
            odometerReadingRecordId: media.OdometerReadingRecordId,
            photoType: media.PhotoType,
            uploadStatus: media.UploadStatus
          });
        }
        // Keep Failed and PendingUpload media that are not orphaned
        else {
          this.logService.debug('OdometerReadingMediaCleanup.MediaPreserved', { 
            mediaRecordId: media.RecordId, 
            odometerReadingRecordId: media.OdometerReadingRecordId,
            uploadStatus: media.UploadStatus,
            reason: 'Not orphaned and not uploaded'
          });
        }
      }

      // Delete all identified media records
      if (mediaToDelete.length > 0) {
        for (const media of mediaToDelete) {
          if (media.RecordId) {
            await this.odometerReadingMediaService.deleteOdometerReadingMedia(media.RecordId, transaction);
          }
        }
        this.logService.info('OdometerReadingMediaCleanup.Completed', { 
          currentUserId: currentUserId,
          totalMediaRecords: allMedia.length,
          deletedCount: mediaToDelete.length,
          preservedCount: allMedia.length - mediaToDelete.length
        });
      } else {
        this.logService.info('OdometerReadingMediaCleanup.NoCleanupNeeded', { 
          currentUserId: currentUserId,
          totalMediaRecords: allMedia.length
        });
      }

    } catch (error) {
      this.logService.error('OdometerReadingMediaCleanup.Failed', error?.toString() || 'Unknown error');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}